import {
  CornerName,
  DOOR_SWINGS,
  DoorSwing,
  EDGE_SEGMENT_TYPES,
  EdgeSegmentType,
  FloorPlanRoom,
  OBJECT_SUBTYPES,
  ObjectSubtype,
  Point,
} from '@kaiizen/kai-floor-plan';

export const RECT_CORNER: Record<CornerName, { sx: number; sy: number; hN: CornerName; vN: CornerName }> = {
  backLeftCorner: { sx: 1, sy: 1, hN: 'backRightCorner', vN: 'frontLeftCorner' },
  backRightCorner: { sx: -1, sy: 1, hN: 'backLeftCorner', vN: 'frontRightCorner' },
  frontRightCorner: { sx: -1, sy: -1, hN: 'frontLeftCorner', vN: 'backRightCorner' },
  frontLeftCorner: { sx: 1, sy: -1, hN: 'frontRightCorner', vN: 'backLeftCorner' },
};

export const CORNER_POINT: Record<CornerName, (s: FloorPlanRoom) => Point> = {
  backLeftCorner: () => ({ x: 0, y: 0 }),
  backRightCorner: s => ({ x: s.width, y: 0 }),
  frontRightCorner: s => ({ x: s.width, y: s.length }),
  frontLeftCorner: s => ({ x: 0, y: s.length }),
};

// Kai Pro brand light palette — the ONLY source for flooring tints.
const lightOrange = '#f6d0a7';
const lightGreen = '#d8eadd'; // figma: light-verdant
const lightYellow = '#fde99a';
const lightRiverBlue = '#95d5eb'; // figma: light-river-blue
const lightBlue = '#e9fcff';
const offWhiteOrange = '#f8ede1';

// Flooring fills. Index 0 is the default (untyped) floor — white, so the basic
// room reads clean against the gridded canvas. Indices 1+ are brand light
// variants, assigned as distinct flooring TYPES are added so adjacent floor
// types stay distinguishable.
export const FLOOR_PALETTE = ['#ffffff', lightRiverBlue, lightOrange, lightGreen, lightYellow, lightBlue, offWhiteOrange];

export const FLOOR_TYPES = ['Tile', 'Hardwood', 'Carpet', 'Vinyl', 'Laminate', 'Stone', 'Concrete'];

/**
 * One entry in the flooring-type pick list. `key` is what the room STORES
 * (`floorType` fields), `label` is what the UI PRESENTS, and `id` is the host
 * app's identifier for the type — carried through untouched. Hosts override
 * the whole list via the FloorPlanEditor `floorTypes` prop.
 */
export interface FloorTypeOption {
  id: string;
  key: string;
  label: string;
}

/** The built-in pick list, used when the host doesn't pass one. */
export const DEFAULT_FLOOR_TYPE_OPTIONS: FloorTypeOption[] = FLOOR_TYPES.map(t => ({ id: t, key: t, label: t }));

// Moved to @kaiizen/kai-floor-plan (Phase 2, state refactor); re-exported for editor consumers.
export { MIN_FREE_ELEMENT_SIZE } from '@kaiizen/kai-floor-plan';

/** Display labels for every edge-item kind (Record ⇒ exhaustive by type). */
export const EDGE_ITEM_LABELS: Record<EdgeSegmentType, string> = {
  alcove: 'Alcove',
  door: 'Door',
  opening: 'Opening',
  window: 'Window',
};

export const EDGE_ITEM_TYPES: [EdgeSegmentType, string][] = EDGE_SEGMENT_TYPES.map(t => [t, EDGE_ITEM_LABELS[t]]);

/** The edge item types a line edge (an alcove wall or a corner diagonal) can hold: an alcove only cuts into a primary edge. */
export const LINE_EDGE_ITEM_TYPES: [EdgeSegmentType, string][] = EDGE_ITEM_TYPES.filter(([type]) => type !== 'alcove');

/**
 * The names of every door swing (Record ⇒ exhaustive by type): the label its
 * picker shows, and the name of its folder in a 3D export ("Door - …").
 */
export const DOOR_SWING_LABELS: Record<DoorSwing, { picker: string; exportFolder: string }> = {
  inLeft: { picker: 'Single · in · left', exportFolder: 'Swing - In Left' },
  inRight: { picker: 'Single · in · right', exportFolder: 'Swing - In Right' },
  outLeft: { picker: 'Single · out · left', exportFolder: 'Swing - Out Left' },
  outRight: { picker: 'Single · out · right', exportFolder: 'Swing - Out Right' },
  pairIn: { picker: 'Pair · in', exportFolder: 'Pair - In' },
  pairOut: { picker: 'Pair · out', exportFolder: 'Pair - Out' },
  pocketLeft: { picker: 'Pocket · left', exportFolder: 'Pocket - Left' },
  pocketRight: { picker: 'Pocket · right', exportFolder: 'Pocket - Right' },
  bifold: { picker: 'Bifold · 4-panel', exportFolder: 'Bifold - 4-Panel' },
  bypass: { picker: 'Bypass · sliding', exportFolder: 'Bypass' },
  garage: { picker: 'Garage · overhead', exportFolder: 'Garage' },
  slideGlassLeft: { picker: 'Sliding glass · left', exportFolder: 'Sliding Glass - Left' },
  slideGlassRight: { picker: 'Sliding glass · right', exportFolder: 'Sliding Glass - Right' },
};

/** Every door swing with its label, in picker order. */
export const DOOR_SWING_OPTIONS: [DoorSwing, string][] = DOOR_SWINGS.map(swing => [swing, DOOR_SWING_LABELS[swing].picker]);

/** A word as a heading or a picker shows it: its first letter capitalized. */
export const capitalized = (text: string): string => text[0].toUpperCase() + text.slice(1);

/** Every object subtype with its label, in picker order. */
export const OBJECT_SUBTYPE_OPTIONS: [ObjectSubtype, string][] = OBJECT_SUBTYPES.map(subtype => [subtype, capitalized(subtype)]);
